import { ErrorsMap, FormValidationError } from 'validation-schema-library';

const LIST_ERROR_KEY_PATTERN = /\[(\d+)]/;

const useErrorMapping = (errorsMap: ErrorsMap = {}) => {
  const normalizeError = (error: FormValidationError): string => {
    if (typeof error === 'string') {
      return error;
    }

    if (error === null || error === undefined) {
      return '';
    }

    return String(error);
  };

  const parseError = (error: FormValidationError): { errorKey: string; params: string[] } => {
    const normalizedError = normalizeError(error);
    const [errorKey = '', rawParams = ''] = normalizedError.split(':');

    if (!rawParams) {
      return { errorKey, params: [] };
    }

    if (rawParams.includes('=')) {
      const urlParams = new URLSearchParams(rawParams);
      return { errorKey, params: [...urlParams.values()].map(value => value.trim()) };
    }

    return { errorKey, params: rawParams.split(',').map(param => param.trim()) };
  };

  const getErrorCode = (error: FormValidationError) => {
    const [errorCode] = normalizeError(error).split(':');

    return errorCode;
  };

  const mapErrorMessage = (error: FormValidationError) => {
    const normalizedError = normalizeError(error);
    let result: string = normalizedError;
    const { errorKey, params } = parseError(error);

    Object.entries(errorsMap).forEach(entry => {
      const [key, handler] = entry;

      if (key === errorKey && typeof handler === 'function') {
        result = handler(...params);
      } else if (key === errorKey) {
        result = handler;
      }
    });

    return result;
  };

  /**
   * Processes each error using errorsMap object
   * Object format:
   * {
   *   key: handler,
   *   ...
   * }
   * each key is a prefix of an error
   * expected error value:
   * `${key}`
   * `${key}:${param1,param2, ... paramN}`
   * `${key}:key1=value1&key2=value2`
   * matched error`s from @errors param will be replaces
   * with result of handler function.
   */
  const mapFormErrors = (errors: ErrorsMap) => {
    const mappedErrors = { ...errors };

    Object.keys(errors).forEach(source => {
      const resultSource = getResultSource(source);
      const errorList = errors[source];
      mappedErrors[resultSource] = errorList.map(mapErrorMessage);
    });
    return mappedErrors;
  };

  /**
   * Map error source for correct mapping error message in the form.
   * For case, then error key from backend contains list value (like as "error[0]": "error message"),
   * return new error key like as "error.0".
   * For case, then error key not contains list value, return source.
   */
  const getResultSource = (source: string) => {
    return LIST_ERROR_KEY_PATTERN.test(source) ? source.replace(LIST_ERROR_KEY_PATTERN, '.$1') : source;
  };

  return {
    mapFormErrors,
    getErrorCode,
    mapErrorMessage,
  };
};

export default useErrorMapping;
